import { useState } from "react";
import CoverPreview, { PILL_STYLES, WaGlyph } from "@/components/CoverPreview";
import { fileUrl } from "@/lib/api";
import { APP_LABELS, gstSuffix, gstInclusive, waUrl, chatNumber, sampleRequestText } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ChevronLeft, ChevronRight, Smartphone, Monitor, Layers, Square, Download, Loader2, MessageCircle, ChevronDown, FileDown, Images } from "lucide-react";

// Pages are laid out at the exact PDF design size (720 × 900 pt → px) and CSS-scaled to the viewer,
// so the on-screen preview mirrors the customer export 1:1.
const PW = 720;
const PH = 900;

function Scaled({ width, children }) {
  const s = width / PW;
  return (
    <div className="overflow-hidden" style={{ width, height: PH * s }}>
      <div className="origin-top-left" style={{ width: PW, height: PH, transform: `scale(${s})` }}>{children}</div>
    </div>
  );
}

function PageFrame({ children, testid }) {
  return (
    <div className="bg-[#F7F2E7] overflow-hidden flex flex-col" style={{ width: PW, height: PH }} data-testid={testid}>
      {children}
    </div>
  );
}

function Footer({ cat, brand, page }) {
  const phone = chatNumber(cat, brand);
  const wa = waUrl(phone, `Hello Prime Gold Tea, I am viewing Catalogue ${cat?.catalogue_number || ""} (page ${page}) and would like to chat about your teas.`);
  const st = PILL_STYLES[cat?.cover?.number_placement?.chat_style] || PILL_STYLES.green;
  return (
    <div className="mt-auto shrink-0 bg-[#0B3B2E] border-t-2 border-[#C9A34A] flex items-center justify-between text-[#F7F2E7]" style={{ height: 46, padding: "0 20px", fontSize: 8 }}>
      <div>
        <div className="font-semibold">{brand?.company}</div>
        <div className="text-[#E3C77C] font-mono-id" style={{ fontSize: 7 }}>{cat?.catalogue_number}</div>
      </div>
      {wa && (
        <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full font-bold whitespace-nowrap" style={{ fontSize: 9, padding: "5px 13px 5px 12px", gap: 8, background: st.bg, color: st.fg }} data-testid={`footer-chat-pill-${page}`}>
          <WaGlyph color={st.icon} hole={st.bg} size="14px" /> Tap to chat on WhatsApp{phone ? <span className="font-semibold opacity-90">· {phone}</span> : null}
        </a>
      )}
      <div className="text-right">
        <div style={{ fontSize: 7 }}>{brand?.website}</div>
        <div className="text-[#E3C77C] font-semibold">Page {page}</div>
      </div>
    </div>
  );
}

function AuthBand({ brand }) {
  return (
    <div className="shrink-0 bg-[#1D4837] border-b-2 border-[#C9A34A] flex items-center" style={{ height: 82, padding: "0 36px", gap: 18 }} data-testid="preview-auth-band">
      <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="shrink-0 object-contain" style={{ width: 68, height: 62 }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center" style={{ gap: 14 }}>
          <div className="rounded bg-[#C9A34A] text-[#0B3B2E] font-bold whitespace-nowrap" style={{ fontSize: 9.5, padding: "3px 10px", letterSpacing: "0.02em" }}>AUTHORISED DISTRIBUTOR BY TEA BOARD OF INDIA</div>
          <div className="flex items-center whitespace-nowrap" style={{ gap: 5 }}>
            <img src="/teaboard-seal.png" alt="seal" className="object-contain" style={{ width: 20, height: 20 }} />
            <span className="text-[#F7F2E7] font-semibold" style={{ fontSize: 9.5 }}>Licence no. {brand?.licence_number || "TB|LC|DI-10237"}</span>
          </div>
        </div>
        <div className="rounded border border-[#C9A34A] bg-[#C9A34A]/15" style={{ marginTop: 6, padding: "4px 10px" }}>
          <p className="text-[#F3E6BE] italic font-serif" style={{ fontSize: 9, lineHeight: "11.5px" }}>This tea liquor image is completely natural and free from digital optimization. It shows the authentic color and clarity captured live during the photo session.</p>
        </div>
      </div>
    </div>
  );
}

function RateBox({ cat, p }) {
  if (!cat?.show_prices || !p.price_per_kg) return null;
  const incl = cat?.show_gst_inclusive ? gstInclusive(p) : "";
  const avail = cat?.show_availability && p.available_qty ? p.available_qty : "";
  return (
    <div className="bg-[#14513D] text-[#F7F2E7] rounded font-semibold" style={{ padding: "5px 8px", fontSize: 11, marginTop: 2 }}>
      Rate: Rs {p.price_per_kg} / kg{gstSuffix(p)}{!incl && avail ? `  ·  ${avail}` : ""}
      {incl && (
        <div className="text-[#E3C77C] font-normal" style={{ fontSize: 8, marginTop: 2 }}>
          {[incl, avail ? `${avail} available` : ""].filter(Boolean).join("  ·  ")}
        </div>
      )}
    </div>
  );
}

function ProductPage({ cat, p, idx, brand, page }) {
  const apps = (p.best_application || []).map((a) => APP_LABELS[a] || a);
  const details = [
    ["Manufacturer / Supplier", p.garden],
    ["Invoice / Lot", [p.invoice_no, p.lot_no].filter(Boolean).join(" / ")],
    ["Origin", p.origin],
    ["Cup / Liquor", p.liquor_color],
    ["Body & Strength", [p.body, p.strength].filter(Boolean).join(" · ")],
    ["Briskness", p.briskness],
    ...(apps.length ? [["Best Application", apps.join(", ")]] : []),
  ].filter(([, v]) => v);
  const isPdf = p.report_content_type === "application/pdf";
  const n = p.position || idx + 1;
  const wa = waUrl(chatNumber(cat, brand), sampleRequestText(p, n, cat?.catalogue_number));

  return (
    <PageFrame testid={`preview-product-${idx}`}>
      <AuthBand brand={brand} />
      <div className="shrink-0 bg-[#0B3B2E] border-b-2 border-[#C9A34A] flex items-center" style={{ height: 60, padding: "0 36px 0 20px", gap: 12 }}>
        <div className="grid place-items-center rounded-full bg-[#C9A34A] text-[#0B3B2E] font-bold shrink-0" style={{ width: 36, height: 36, fontSize: 16 }}>{n}</div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[#F7F2E7] leading-tight truncate" style={{ fontSize: 20 }}>{p.brand_group || p.garden || p.name || "Tea Sample"}</div>
          <div className="text-[#E3C77C]" style={{ fontSize: 10, marginTop: 3 }}>{[p.grade, p.origin].filter(Boolean).join(" · ")}</div>
        </div>
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" className="shrink-0 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-bold whitespace-nowrap" style={{ fontSize: 9, padding: "6px 13px" }} data-testid={`preview-sample-request-${idx}`}>
            Request a sample on WhatsApp
          </a>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col" style={{ padding: "12px 36px 6px" }}>
        <div className="flex items-start" style={{ gap: 22 }}>
          <div className="shrink-0">
            <div className="rounded-lg border border-[#C9A34A] bg-white grid place-items-center overflow-hidden" style={{ width: 280, height: 280, padding: 6 }}>
              {p.liquor_image_url ? (
                <img src={fileUrl(p.liquor_image_url)} alt="liquor" className="w-full h-full object-contain" />
              ) : (
                <span className="text-[#14513D]/60" style={{ fontSize: 10 }}>Liquor image</span>
              )}
            </div>
            <div className="text-[#14513D] font-bold" style={{ fontSize: 8, marginTop: 4, marginLeft: 2 }}>LIQUOR TASTING</div>
          </div>
          <div className="min-w-0 flex-1">
            {details.map(([k, v]) => (
              <div key={k} style={{ marginBottom: 5 }}>
                <div className="text-[#C9A34A] font-bold uppercase" style={{ fontSize: 8, marginBottom: 2 }}>{k}</div>
                <div className="text-[#17251F]" style={{ fontSize: 10, lineHeight: "13px" }}>{v}</div>
              </div>
            ))}
            <RateBox cat={cat} p={p} />
          </div>
        </div>

        {p.selling_note && (
          <div className="rounded-md bg-[#F6E3A1] font-display italic text-[#0B3B2E]" style={{ marginTop: 2, padding: "5px 12px 5px 16px", fontSize: 11, lineHeight: "14px", borderLeft: "5px solid #C9A34A" }} data-testid="selling-note">“{p.selling_note}”</div>
        )}

        <div className="text-[#C9A34A] font-bold" style={{ fontSize: 9, marginTop: 4 }}>COMPLETE TEA TASTING REPORT</div>
        {/* frame hugs the report — no blank letterbox, as large as the page allows, never cropped */}
        <div className="flex-1 min-h-0 relative" style={{ marginTop: 3 }}>
          <div className="absolute inset-0 flex justify-center items-start">
            {p.report_url ? (
              isPdf ? (
                <iframe title="report" src={fileUrl(p.report_url)} className="w-full h-full bg-white border border-[#C9A34A]" />
              ) : (
                <img src={fileUrl(p.report_url)} alt="report" className="max-w-full max-h-full object-contain bg-white border border-[#C9A34A]" style={{ padding: 4 }} data-testid="preview-report-image" />
              )
            ) : (
              <div className="w-full h-full bg-white border border-[#C9A34A] grid place-items-center text-[#14513D]/60" style={{ fontSize: 11 }}>Tasting report</div>
            )}
          </div>
        </div>
      </div>
      <Footer cat={cat} brand={brand} page={page} />
    </PageFrame>
  );
}

export default function CataloguePreview({ cat, brand, onDownload, onShare }) {
  const [active, setActive] = useState(0);
  const [device, setDevice] = useState("mobile");
  const [zoom, setZoom] = useState(100);
  const [view, setView] = useState("all");
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const doDownload = async (kind) => {
    setDownloading(true);
    try { await onDownload(kind); } finally { setDownloading(false); }
  };
  const doShare = async () => {
    setSharing(true);
    try { await onShare(); } finally { setSharing(false); }
  };

  const pages = [
    { key: "cover", label: "Cover", render: () => <CoverPreview cat={cat} brand={brand} fixedNumber /> },
    ...(cat?.products || []).map((p, i) => ({
      key: `p${p.id || i}`,
      label: `Product ${p.position || i + 1}`,
      render: (pg) => <ProductPage cat={cat} p={p} idx={i} brand={brand} page={pg} />,
    })),
  ];

  const clamp = (n) => Math.max(0, Math.min(pages.length - 1, n));
  const maxW = device === "mobile" ? 380 : 620;
  const viewW = (maxW * zoom) / 100;

  const gotoThumb = (i) => {
    setActive(i);
    if (view === "all") document.getElementById(`preview-page-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[7rem_1fr] gap-4" data-testid="catalogue-preview">
      {/* thumbnails */}
      <div className="hidden md:flex flex-col gap-2 max-h-[78vh] overflow-y-auto pr-1">
        {pages.map((pg, i) => (
          <button
            key={pg.key}
            onClick={() => gotoThumb(i)}
            data-testid={`preview-thumb-${i}`}
            className={`rounded-lg overflow-hidden border-2 transition-all ${active === i ? "border-[#C9A34A]" : "border-transparent opacity-70 hover:opacity-100"}`}
          >
            <div className="pointer-events-none">
              <Scaled width={96}>{pg.render(i === 0 ? 1 : i + 1)}</Scaled>
            </div>
            <div className="text-[10px] py-0.5 text-center text-[#14513D] bg-[#FAF7F0]">{pg.label}</div>
          </button>
        ))}
      </div>

      {/* main viewer */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button size="sm" variant={view === "all" ? "default" : "outline"} onClick={() => setView("all")} data-testid="preview-view-all" className="gap-1">
            <Layers className="h-4 w-4" /> All pages
          </Button>
          <Button size="sm" variant={view === "single" ? "default" : "outline"} onClick={() => setView("single")} data-testid="preview-view-single" className="gap-1">
            <Square className="h-4 w-4" /> Single
          </Button>

          {view === "single" ? (
            <>
              <Button size="icon" variant="outline" onClick={() => setActive((a) => clamp(a - 1))} data-testid="preview-prev" disabled={active === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium text-[#14513D]" data-testid="preview-page-label">
                {pages[active]?.label} · {active + 1}/{pages.length}
              </span>
              <Button size="icon" variant="outline" onClick={() => setActive((a) => clamp(a + 1))} data-testid="preview-next" disabled={active === pages.length - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <span className="text-sm font-medium text-[#14513D]" data-testid="preview-total-label">
              Full catalogue · {pages.length} pages
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            {onShare && (
              <Button
                size="sm"
                onClick={doShare}
                disabled={sharing}
                data-testid="preview-share-whatsapp"
                className="gap-1 bg-[#25D366] hover:bg-[#1FB855] text-white"
              >
                {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} Share to WhatsApp
              </Button>
            )}
            {onDownload && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" disabled={downloading} data-testid="preview-download-pdf" className="gap-1 bg-[#0B3B2E] hover:bg-[#14513D] text-[#F7F2E7]">
                    {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => doDownload("whatsapp")} data-testid="preview-download-whatsapp"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp PDF (small, &lt;8 MB)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doDownload("hq")} data-testid="preview-download-hq"><FileDown className="h-4 w-4 mr-2" /> High-quality PDF</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doDownload("jpg")} data-testid="preview-download-jpg"><Images className="h-4 w-4 mr-2" /> JPG pages (ZIP)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button size="icon" variant={device === "mobile" ? "default" : "outline"} onClick={() => setDevice("mobile")} data-testid="preview-mobile">
              <Smartphone className="h-4 w-4" />
            </Button>
            <Button size="icon" variant={device === "desktop" ? "default" : "outline"} onClick={() => setDevice("desktop")} data-testid="preview-desktop">
              <Monitor className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 w-32">
              <span className="text-xs text-[#14513D]">{zoom}%</span>
              <Slider value={[zoom]} min={60} max={150} step={5} onValueChange={(v) => setZoom(v[0])} data-testid="preview-zoom" />
            </div>
          </div>
        </div>

        {view === "single" ? (
          <div className="rounded-xl bg-[#0B3B2E]/5 p-4 flex justify-center overflow-auto max-h-[75vh]">
            <div className="shadow-2xl rounded-lg overflow-hidden border border-[#C9A34A]/30 shrink-0">
              <Scaled width={viewW}>{pages[active]?.render(active === 0 ? 1 : active + 1)}</Scaled>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-[#0B3B2E]/5 p-4 overflow-auto max-h-[78vh]" data-testid="preview-all">
            <div className="flex flex-col items-center gap-6">
              {pages.map((pg, i) => (
                <div key={pg.key} id={`preview-page-${i}`} className="w-full flex flex-col items-center scroll-mt-4">
                  <div className="shadow-2xl rounded-lg overflow-hidden border border-[#C9A34A]/30 shrink-0" data-testid={`preview-full-page-${i}`}>
                    <Scaled width={viewW}>{pg.render(i === 0 ? 1 : i + 1)}</Scaled>
                  </div>
                  <div className="mt-1.5 text-xs text-[#14513D]/60">{pg.label} · {i + 1}/{pages.length}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
